"use client";

import { useEffect } from "react";
import { useAnalytics } from "@/components/analytics/use-analytics";

/**
 * Component that sets up global error handlers for client-side errors
 * This should be mounted once at the root of your application
 */
export default function GlobalErrorHandler() {
  const { captureException } = useAnalytics();

  useEffect(() => {
    // Handle uncaught errors
    const handleError = (event: ErrorEvent) => {
      // Cross-origin scripts (third-party ad/analytics tags such as the Google
      // Ads gtag.js) surface as an opaque "Script error." with no error object,
      // message detail, or usable stack — the browser masks them for security.
      // There is nothing actionable to report, so drop it instead of polluting
      // error tracking (#86).
      if (!event.error && (event.message ?? "").trim() === "Script error.") {
        return;
      }

      const error = event.error || new Error(event.message);

      console.error(error, {
        source: "window.onerror",
        type: "uncaught_error",
        location: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
      captureException(error, "window.onerror", {
        type: "uncaught_error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      console.error(error, {
        source: "unhandledrejection",
        type: "unhandled_promise",
        reason: event.reason?.toString?.() || "Unknown promise rejection",
      });
      captureException(error, "unhandledrejection", {
        type: "unhandled_promise",
      });
    };

    // Set up the event listeners
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    console.info("Global error handlers initialized");

    // Clean up the event listeners when the component unmounts
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [captureException]);

  // This component doesn't render anything
  return null;
}
